import React, { useState, useContext, useEffect } from "react";
import { Button, Card, Row, Col, Container, Modal, Form } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { FaPhoneAlt, FaMapMarkerAlt, FaArrowLeft, FaShareAlt, FaWhatsapp, FaCopy, FaCheck, FaFacebook, FaTwitter, FaComments } from "react-icons/fa";
import EnquiryQuoteModal from "../components/EnquiryQuoteModal";
import LeadCaptureModal from "../components/LeadCaptureModal";
import { Helmet } from "react-helmet";
import "./Products.css";
import { CartContext } from "./CartContext";
import config from "../config/config";
import defaultLogo from "../assets/zen_logo.png";

const ProductDetails = () => {
  const { API_DOMAIN, subdomain } = config;
  const navigate = useNavigate();
  const location = useLocation();
  const { product } = location.state || {};
  const [productData, setProductData] = useState(product || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const { cart, addToCart, increaseQty, decreaseQty, removeItem, storeTheme } =
    useContext(CartContext);
  const companyId = subdomain;

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [zoomStyle, setZoomStyle] = useState({ transformOrigin: 'center center', transform: 'scale(1)' });

  // Read URL query parameter if direct link
  const queryParams = new URLSearchParams(location.search);
  const targetProdId = queryParams.get("id") || queryParams.get("product_id") || queryParams.get("p");

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transformOrigin: `${x}% ${y}%`,
      transform: 'scale(2)'
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({
      transformOrigin: 'center center',
      transform: 'scale(1)'
    });
  };

  useEffect(() => {
    const fetchProductDetailsAndCategories = async () => {
      setLoading(true);
      setError(null);

      try {
        let currentProd = productData;

        // If product was not in location.state (e.g. opened from shared link), fetch from API
        if ((!currentProd || currentProd.gst_percent === undefined) && targetProdId) {
          try {
            const prodRes = await fetch(`${API_DOMAIN}/product.php`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ company_id: companyId, fetch_all: "true", product_id: targetProdId }),
            });
            if (prodRes.ok) {
              const prodJson = await prodRes.json();
              if (prodJson.head?.code === 200 && prodJson.body?.products?.length > 0) {
                const found = prodJson.body.products.find(
                  (p) => String(p.product_id) === String(targetProdId) || String(p.id) === String(targetProdId)
                ) || prodJson.body.products[0];
                currentProd = found;
              }
            }
          } catch (e) {
            console.error("Error fetching product by ID:", e);
          }
        }

        if (!currentProd) {
          setLoading(false);
          return;
        }

        // 1. Fetch Categories
        const catPayload = { company_id: companyId, fetch_all: "true" };
        const catRes = await fetch(`${API_DOMAIN}/category.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(catPayload),
        });

        let apiCatsRaw = [];
        if (catRes.ok) {
          const catData = await catRes.json();
          if (catData.head.code === 200 && catData.body.categories) {
            apiCatsRaw = catData.body.categories;
            const apiCats = apiCatsRaw.map((cat) => ({
              id: String(cat.category_code),
              name: cat.category_name,
              discount: parseFloat(cat.discount) || 0,
            }));
            setCategories(apiCats);
          }
        }

        // Re-calculate price and discount
        const originalPrice = parseFloat(currentProd.original_price) || parseFloat(currentProd.product_price) || 0;
        const productDisc = parseFloat(currentProd.product_disc) || parseFloat(currentProd.discount) || 0;
        const discountLocked = currentProd.discount_locked === 1 || currentProd.discount_locked === "1" || currentProd.discount_locked === true;

        const catItem = apiCatsRaw.find(c => String(c.category_code) === String(currentProd.category_code));
        const categoryDisc = catItem ? (parseFloat(catItem.discount) || 0) : 0;

        const effectiveDisc = (discountLocked || categoryDisc === 0) ? productDisc : categoryDisc;
        const finalPrice = effectiveDisc > 0 ? (originalPrice - (originalPrice * effectiveDisc / 100)) : originalPrice;

        setProductData({
          ...currentProd,
          original_price: originalPrice,
          discount: effectiveDisc,
          price: parseFloat(finalPrice.toFixed(2)) || 0,
          hsn_code: currentProd.hsn_code || "",
          gst_percent: parseFloat(currentProd.gst_percent) || 0,
          gst_type: currentProd.gst_type || "inclusive",
        });

        // 2. Fetch Company Details
        const companyPayload = { search_text: "company", company_id: companyId };
        const companyRes = await fetch(`${API_DOMAIN}/company.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(companyPayload),
        });

        if (companyRes.ok) {
          const companyData = await companyRes.json();
          if (companyData.head.code === 200 && companyData.body.companies) {
            const company = companyData.body.companies[0];
            const rawCompanyLogo = company.img && company.img.trim() !== "" && company.img !== "null" ? company.img : "";
            const formattedCompany = {
              title: company.company_name,
              address: `${company.address}, ${company.city} - ${company.pincode}`,
              contactNumbers: [company.phone, company.mobile].filter(Boolean),
              logo: rawCompanyLogo || defaultLogo,
              rawLogo: rawCompanyLogo,
            };
            setCompanyDetails(formattedCompany);
          } else {
            setCompanyDetails(null);
          }
        }
      } catch (err) {
        console.error("Error in fetchProductDetailsAndCategories:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetailsAndCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_DOMAIN, companyId, targetProdId]);

  const getShareUrl = () => {
    const pId = productData?.product_id || productData?.id || targetProdId;
    return `${API_DOMAIN}/share.php?p=${encodeURIComponent(pId)}&c=${encodeURIComponent(subdomain)}`;
  };

  const handleShareProduct = () => {
    if (!productData) return;
    const shareUrl = getShareUrl();
    const storeTitle = companyDetails?.title || "Zen Online Store";
    const shareText = `Check out *${productData.product_name}* at ₹${productData.price} on ${storeTitle}!\n\nView details & order online:`;

    if (navigator.share) {
      navigator.share({
        title: `${productData.product_name} - ${storeTitle}`,
        text: shareText,
        url: shareUrl,
      }).catch((err) => {
        if (err.name !== 'AbortError') {
          setShowShareModal(true);
        }
      });
    } else {
      setShowShareModal(true);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = getShareUrl();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2500);
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    }
  };

  const handleBack = () => {
    // If user arrived from another page within the store session, go back
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      // Direct link / WhatsApp shared link landing fallback to store home
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className={`product-details-page-wrapper ${storeTheme}`}>
        <Container className="productList py-5 text-center">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="product-state-message mt-3">Loading product details...</div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`product-details-page-wrapper ${storeTheme}`}>
        <Container className="productList py-5 text-center">
          <div className="product-state-message text-danger">Error: {error}</div>
          <Button variant="primary" className="mt-3" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </Container>
      </div>
    );
  }

  if (!productData) {
    return (
      <div className={`product-details-page-wrapper ${storeTheme}`}>
        <Container className="productList py-5 text-center">
          <div className="product-state-message">No product data found.</div>
          <Button variant="primary" className="mt-3" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </Container>
      </div>
    );
  }

  const cartItem = cart.find((item) => item.id === productData.id);
  const qty = cartItem ? cartItem.qty : 0;

  const handleAddToCart = () => {
    addToCart(productData);
  };

  const fallbackProductImg = companyDetails?.rawLogo || defaultLogo;
  const sellerInfo = companyDetails || {
    title: "Company",
    address: "Not Available",
    contactNumbers: [],
    logo: defaultLogo,
  };

  const cartTotalItems = cart.reduce((total, item) => total + item.qty, 0);
  const cartTotalPrice = cart.reduce(
    (total, item) => total + item.price * item.qty,
    0
  );

  let images = [];
  if (productData.product_img_urls) {
    if (Array.isArray(productData.product_img_urls)) {
      images = productData.product_img_urls;
    } else if (typeof productData.product_img_urls === 'string') {
      try {
        images = JSON.parse(productData.product_img_urls);
      } catch (e) {
        images = [productData.product_img_urls];
      }
    }
  }
  if (images.length === 0 && productData.product_img_url) {
    images = [productData.product_img_url];
  }
  images = images.filter(Boolean);
  if (images.length === 0) {
    images = [fallbackProductImg];
  }

  const primaryImage = images[0] || fallbackProductImg;
  const storeTitle = companyDetails?.title || "Zen Online Store";
  const shareUrl = getShareUrl();
  const shareText = `Check out *${productData.product_name}* at ₹${productData.price} on ${storeTitle}!\n\nView details & order online:`;

  return (
    <div className={`product-details-page-wrapper ${storeTheme}`}>
      {/* Advance Meta Tags via React Helmet */}
      <Helmet>
        <title>{`${productData.product_name} - ${storeTitle}`}</title>
        <meta name="description" content={productData.description || `Buy ${productData.product_name} online at best price ₹${productData.price} from ${storeTitle}.`} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content={storeTitle} />
        <meta property="og:title" content={`${productData.product_name} | ₹${productData.price} - ${storeTitle}`} />
        <meta property="og:description" content={productData.description || `Buy ${productData.product_name} online at best price ₹${productData.price} from ${storeTitle}.`} />
        <meta property="og:image" content={primaryImage} />
        <meta property="og:image:secure_url" content={primaryImage} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="800" />
        <meta property="og:image:alt" content={productData.product_name} />
        <meta property="og:url" content={window.location.href} />
        <meta property="product:price:amount" content={String(productData.price)} />
        <meta property="product:price:currency" content="INR" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={storeTitle} />
        <meta name="twitter:title" content={`${productData.product_name} | ₹${productData.price}`} />
        <meta name="twitter:description" content={productData.description || `Buy ${productData.product_name} online at best price ₹${productData.price}.`} />
        <meta name="twitter:image" content={primaryImage} />
        <meta name="twitter:image:alt" content={productData.product_name} />

        {/* Schema.org JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": productData.product_name,
            "image": images,
            "description": productData.description || `Buy ${productData.product_name} online at ₹${productData.price}`,
            "sku": productData.product_code || productData.product_id,
            "offers": {
              "@type": "Offer",
              "url": window.location.href,
              "priceCurrency": "INR",
              "price": String(productData.price),
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>

      <Container className="productList py-4">
        <Button
          variant="primary"
          className="btn-back mb-3"
          onClick={handleBack}
          aria-label="Back to catalog"
          title="Back to catalog"
        >
          <FaArrowLeft />
        </Button>

        <Row>
          <Col
            lg={5}
            md={5}
            className="text-center d-flex flex-column flex-lg-row gap-3 mt-3"
          >
            {/* Thumbnail gallery */}
            {images.length > 1 && (
              <div className="thumbnail-list d-flex flex-row flex-lg-column gap-2 mb-3 mb-lg-0 order-2 order-lg-1 justify-content-center">
                {images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`thumbnail-item ${idx === activeImageIndex ? 'active' : ''}`}
                    onClick={() => setActiveImageIndex(idx)}
                    onError={(e) => { e.currentTarget.src = fallbackProductImg; }}
                  />
                ))}
              </div>
            )}

            {/* Main Image with Zoom */}
            <div
              className="big-img zoom-container w-100 flex-grow-1 d-flex order-1 order-lg-2"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in', maxHeight: '450px' }}
            >
              <img
                src={images[activeImageIndex] || fallbackProductImg}
                alt={productData.product_name}
                className="img-fluid rounded border zoom-image w-100"
                onError={(e) => {
                  if (e.currentTarget.src !== fallbackProductImg) {
                    e.currentTarget.src = fallbackProductImg;
                  } else {
                    e.currentTarget.src = defaultLogo;
                  }
                }}
                style={{
                  ...zoomStyle,
                  objectFit: 'contain',
                  maxHeight: '450px'
                }}
              />
            </div>
          </Col>

          <Col lg={7} md={7}>
            <div className="d-flex justify-content-between align-items-start gap-2 pt-3">
              <h3 className="product-title m-0">{productData.product_name}</h3>
              <Button
                variant="outline-success"
                className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-pill shadow-sm text-nowrap"
                style={{ fontSize: "13px", fontWeight: "600", borderColor: "#22c55e", color: "#16a34a" }}
                onClick={handleShareProduct}
                title="Share this product"
              >
                <FaShareAlt /> Share
              </Button>
            </div>

            <div className="d-flex align-items-baseline gap-2 flex-wrap mb-2 mt-2">
              <h4 className="product-price-val m-0">₹{productData.price}</h4>
              {Number(productData.gst_percent || 0) > 0 && (
                <span className="badge bg-light text-secondary border align-self-center ms-1" style={{ fontSize: '12px' }}>
                  {productData.gst_type === 'exclusive' ? `+ ${productData.gst_percent}% GST (Excl.)` : `Incl. ${productData.gst_percent}% GST`}
                </span>
              )}
              {productData.discount > 0 && (
                <>
                  <span className="text-decoration-line-through text-muted" style={{ fontSize: '15px' }}>₹{productData.original_price}</span>
                  <span className="text-danger fw-bold" style={{ fontSize: '15px' }}>({productData.discount}% OFF)</span>
                </>
              )}
            </div>

            <div className="product-action-btn-group d-flex align-items-center gap-2 mt-3 flex-wrap">
              {qty > 0 ? (
                <div className="quantity-controls product-qty-controls">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (qty === 1) {
                        removeItem(productData.id);
                      } else {
                        decreaseQty(productData.id);
                      }
                    }}
                  >
                    –
                  </Button>
                  <span className="qty-count mx-2">{qty}</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      increaseQty(productData.id);
                    }}
                  >
                    +
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline-primary"
                  className="cart product-add-cart-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart();
                  }}
                >
                  + Add to Cart
                </Button>
              )}

              <Button
                variant="success"
                className="product-wa-btn d-inline-flex align-items-center justify-content-center gap-1.5 text-white shadow-sm"
                onClick={() => {
                  window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`, '_blank');
                }}
              >
                <FaWhatsapp size={16} /> <span>WhatsApp Share</span>
              </Button>
            </div>

            <div className="mt-4">
              <Row className="d-flex justify-content-between align-items-center">
                <Col lg="6" md="6" xs={12} className="mt-3 section-title-col">
                  <h5>About Product:</h5>
                </Col>
              </Row>
              <Row className="d-flex justify-content-between align-items-center">
                <Col lg="6" md="6" xs={12} className="mt-3 info-label">
                  ITEM CODE :{" "}
                  <span className="info-value">{productData.product_code}</span>
                </Col>
                <Col lg="6" md={6} xs={12} className="mt-3 info-label">
                  CATEGORY :{" "}
                  <span className="info-value">
                    {categories.find(
                      (cat) => cat.id === productData.category_code
                    )?.name || "Unknown"}
                  </span>
                </Col>
              </Row>
            </div>

            {productData.description && (
              <div className="mt-4">
                <h5 className="section-title-col">Description:</h5>
                <p
                  style={{
                    whiteSpace: 'pre-line',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    opacity: 0.9
                  }}
                >
                  {productData.description}
                </p>
              </div>
            )}

            <div className="d-none d-lg-block">
              <Row className="mt-4">
                <Col lg="6" md="6" xs={12} className="mt-3 section-title-col">
                  <h2 className="fs-6">Seller</h2>
                </Col>
              </Row>
              <Card className="p-3 mt-2">
                <Row>
                  <Col md={9}>
                    <p>
                      <strong>{sellerInfo.title}</strong>
                    </p>
                    <p>
                      <FaMapMarkerAlt style={{ marginRight: "5px" }} />
                      {sellerInfo.address}
                    </p>
                    <p className="contact-box">
                      <FaPhoneAlt className="icon" />
                      {sellerInfo.contactNumbers.join(", ")}
                    </p>
                  </Col>
                  <Col md={3} className="text-center">
                    <img
                      src={sellerInfo.logo || defaultLogo}
                      alt={sellerInfo.title || "Company Logo"}
                      className="img-fluid"
                      style={{ maxWidth: "80px" }}
                      onError={(e) => { e.currentTarget.src = defaultLogo; }}
                    />
                  </Col>
                </Row>
              </Card>
            </div>
          </Col>
        </Row>

        {cart.length > 0 && (
          <div className="cart-bar">
            <div
              className="cart-info-clickable"
              onClick={() => navigate("/orderpage")}
              style={{ cursor: "pointer" }}
            >
              <span>{cartTotalItems} ITEMS</span>
              <span className="separator"> . </span>
              <span>₹ {cartTotalPrice.toFixed(2)}</span>
            </div>
            <div className="cart-action position-relative">
              <span
                className="cart-icon"
                onClick={() => navigate("/orderpage")}
                style={{ cursor: "pointer" }}
              >
                🛒
              </span>

              {cartTotalItems > 0 && (
                <span className="cart-badge">{cartTotalItems}</span>
              )}

              <Button onClick={() => navigate("/orderpage")}>View cart</Button>
            </div>
          </div>
        )}
      </Container>

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered className="share-product-modal">
        <Modal.Header closeButton className="border-0 pb-1">
          <Modal.Title className="fs-6 fw-bold">Share Product</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <div className="d-flex align-items-center gap-3 p-2 bg-light rounded-3 mb-3 border">
            <img
              src={primaryImage}
              alt={productData.product_name}
              style={{ width: "54px", height: "54px", objectFit: "contain", borderRadius: "8px", background: "#fff" }}
              onError={(e) => { e.currentTarget.src = fallbackProductImg; }}
            />
            <div className="flex-grow-1 overflow-hidden">
              <div className="fw-bold text-truncate" style={{ fontSize: "14px" }}>{productData.product_name}</div>
              <div className="text-success fw-bold" style={{ fontSize: "13px" }}>₹{productData.price}</div>
              <div className="text-muted small" style={{ fontSize: "11px" }}>{storeTitle}</div>
            </div>
          </div>

          <div className="d-grid gap-2 mb-3">
            <Button
              variant="success"
              className="d-flex align-items-center justify-content-center gap-2 py-2 fw-semibold"
              style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
              onClick={() => {
                window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`, '_blank');
              }}
            >
              <FaWhatsapp size={18} /> Share on WhatsApp
            </Button>

            <div className="d-flex gap-2">
              <Button
                variant="primary"
                className="w-50 d-flex align-items-center justify-content-center gap-2 py-2 small fw-semibold"
                style={{ backgroundColor: "#1877F2", borderColor: "#1877F2" }}
                onClick={() => {
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
                }}
              >
                <FaFacebook size={16} /> Facebook
              </Button>
              <Button
                variant="dark"
                className="w-50 d-flex align-items-center justify-content-center gap-2 py-2 small fw-semibold"
                onClick={() => {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
                }}
              >
                <FaTwitter size={16} /> X (Twitter)
              </Button>
            </div>
          </div>

          <Form.Group>
            <Form.Label className="small text-muted fw-bold mb-1">Product Link</Form.Label>
            <div className="input-group">
              <Form.Control
                readOnly
                value={shareUrl}
                style={{ fontSize: "12px", backgroundColor: "#f8fafc" }}
              />
              <Button
                variant={copySuccess ? "success" : "outline-secondary"}
                onClick={handleCopyLink}
                className="d-flex align-items-center gap-1 px-3"
                style={{ fontSize: "13px" }}
              >
                {copySuccess ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
      </Modal>

      {/* 🎡 Floating "Spin & Win Offer!" Capsule Button & Modal */}
      <LeadCaptureModal
        company={companyDetails}
        API_DOMAIN={API_DOMAIN}
        subdomain={subdomain}
        hasCart={cart && cart.length > 0}
      />
    </div>
  );
};

export default ProductDetails;
